if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      static PRESERVE_SIZE_PARAM = 'preserve_size';
      static PRESERVE_SIZE_KEY = 'criquet:preserveSize';

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.bindColorSwatchSizeCarry();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));

        // Faux color swatches are sibling-product <a> links (full page load).
        // Restore size after variant-selects has connected.
        requestAnimationFrame(() => {
          this.restorePreservedSizeFromNavigation();
          this.syncSizeOntoColorLinks();
        });
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        // A selection is only "complete" once every option (including Size, which
        // is intentionally not pre-selected) has a chosen value. Until then the
        // resolved variant is treated as null so ATC stays gated on Size products.
        const optionCount = this.variantSelectors?.querySelectorAll('.product-form__input').length ?? 0;
        this.isSelectionComplete = optionCount > 0 && selectedOptionValues.length >= optionCount;

        this.syncSizeOntoColorLinks();
        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        // Color is a different product URL, so the swap replaces <main>/product-info
        // and would drop Size. Capture the shopper's required picks (by value
        // string, not option-value id — those ids don't survive a product swap)
        // and re-apply after the new markup is in the DOM.
        const selectedRequired = this.getSelectedRequiredValues();

        return (html) => {
          this.productModal?.remove();

          const restoreSizeAfterSwap = (newNode) => {
            const productInfo = updateFullPage
              ? newNode.querySelector("product-info[id^='MainProduct']")
              : newNode;
            const variantSelects = productInfo?.querySelector('variant-selects');
            if (!variantSelects) return;

            this.applyRestoredSize(variantSelects, selectedRequired);
          };

          const postCallbacks = [...this.postProcessHtmlCallbacks, restoreSizeAfterSwap];

          // Don't put first-available (wrong size) in the URL; the restored
          // size change handler writes the correct variant id.
          this.updateURL(productUrl);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              postCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              postCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            if (!document.body.classList.contains('prevent-variant-scroll')) {
              document.querySelector(`#${targetId}`)?.focus();
            }
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedRequiredValues() {
        return Array.from(this.variantSelectors?.querySelectorAll('[data-required-option]') ?? []).map(
          (fieldset) => fieldset.querySelector('input:checked, option[selected]')?.value
        );
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          // Required (non-Color) options are never pre-selected server-side, so the
          // refreshed markup would drop the shopper's choices. Capture them (in option
          // order) before the swap and re-apply afterwards so checked radios/selects
          // and their [data-size-label] text persist.
          const selectedRequired = this.getSelectedRequiredValues();

          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks, [
            (newNode) => this.restoreRequiredSelections(newNode, selectedRequired),
          ]);
        }
      }

      restoreRequiredSelections(newVariantSelects, selectedValues) {
        if (!newVariantSelects) return false;

        const fieldsets = newVariantSelects.querySelectorAll('[data-required-option]');
        if (!fieldsets.length) {
          newVariantSelects.syncDependentOptionAvailability?.();
          return false;
        }

        let restoredAll = true;

        fieldsets.forEach((fieldset, index) => {
          const value = selectedValues[index];
          if (!value) {
            restoredAll = false;
            return;
          }

          const input = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
            (radio) =>
              radio.value.toLowerCase() === value.toLowerCase() &&
              !radio.disabled &&
              !radio.classList.contains('disabled')
          );
          if (input) {
            input.checked = true;
            const label = fieldset.querySelector('[data-size-label]');
            if (label) label.textContent = input.value;
            return;
          }

          const select = fieldset.querySelector('select');
          const option = select
            ? Array.from(select.options).find(
                (opt) => opt.value.toLowerCase() === value.toLowerCase() && !opt.disabled
              )
            : null;
          if (option) {
            select.value = option.value;
            Array.from(select.options).forEach((opt) => (opt.selected = opt === option));
            const label = fieldset.querySelector('[data-size-label]');
            if (label) label.textContent = option.value;
            return;
          }

          restoredAll = false;
        });

        newVariantSelects.syncDependentOptionAvailability?.();

        return restoredAll;
      }

      applyRestoredSize(variantSelects, selectedValues) {
        const restored = this.restoreRequiredSelections(variantSelects, selectedValues);
        if (!restored) return false;

        // Re-enter the same-product update path so price, inventory, hidden
        // variant id, ATC, and URL all resolve against the restored size.
        const target =
          variantSelects.querySelector('[data-required-option] input:checked') ||
          variantSelects.querySelector('[data-required-option] select');
        target?.dispatchEvent(new Event('change', { bubbles: true }));

        requestAnimationFrame(() => {
          const sticky = document.querySelector('sticky-atc');
          sticky?.syncFromMain?.();
          sticky?.updateFromMain?.();
          sticky?.checkAvailability?.();
        });

        return true;
      }

      // Each colour is its own product (faux swatches = <a href>). Stash the
      // selected size on those links + sessionStorage so a full page load can
      // restore it — without pre-selecting size on a normal landing.
      bindColorSwatchSizeCarry() {
        this.addEventListener(
          'click',
          (event) => {
            const link = event.target.closest('.options-selector.swatches a[href]');
            if (!link || link.classList.contains('is-active')) return;

            const values = this.getSelectedRequiredValues().filter(Boolean);
            if (!values.length) return;

            try {
              const dest = new URL(link.getAttribute('href'), window.location.origin);
              sessionStorage.setItem(
                ProductInfo.PRESERVE_SIZE_KEY,
                JSON.stringify({ values, dest: dest.pathname })
              );
              dest.searchParams.set(ProductInfo.PRESERVE_SIZE_PARAM, values.join(','));
              link.setAttribute('href', `${dest.pathname}${dest.search}${dest.hash}`);
            } catch (error) {
              console.error(error);
            }
          },
          true
        );
      }

      syncSizeOntoColorLinks() {
        const values = this.getSelectedRequiredValues().filter(Boolean);
        this.querySelectorAll('.options-selector.swatches a[href]').forEach((link) => {
          try {
            const url = new URL(link.getAttribute('href'), window.location.origin);
            if (values.length) {
              url.searchParams.set(ProductInfo.PRESERVE_SIZE_PARAM, values.join(','));
            } else {
              url.searchParams.delete(ProductInfo.PRESERVE_SIZE_PARAM);
            }
            link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
          } catch (error) {
            console.error(error);
          }
        });
      }

      restorePreservedSizeFromNavigation() {
        const values = this.consumePreservedSize();
        if (!values.length || !this.variantSelectors) return;

        this.applyRestoredSize(this.variantSelectors, values);
      }

      consumePreservedSize() {
        const params = new URLSearchParams(window.location.search);
        let values = params.get(ProductInfo.PRESERVE_SIZE_PARAM)?.split(',').filter(Boolean) ?? [];

        let stored = null;
        try {
          stored = JSON.parse(sessionStorage.getItem(ProductInfo.PRESERVE_SIZE_KEY) || 'null');
        } catch (error) {
          stored = null;
        }
        sessionStorage.removeItem(ProductInfo.PRESERVE_SIZE_KEY);

        if (!values.length && stored?.values?.length && stored.dest === window.location.pathname) {
          values = stored.values;
        }

        if (params.has(ProductInfo.PRESERVE_SIZE_PARAM)) {
          params.delete(ProductInfo.PRESERVE_SIZE_PARAM);
          const qs = params.toString();
          window.history.replaceState(
            {},
            '',
            `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`
          );
        }

        return values;
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const hasSizeOption = !!this.variantSelectors?.querySelector('[data-required-option]');

          let variant = this.getSelectedVariant(html);
          // When required (non-Color) options exist, the variant is unresolved until
          // every option is chosen.
          if (hasSizeOption && !this.isSelectionComplete) variant = null;

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          // Incomplete selection on a Size product: keep ATC disabled + "Select Size"
          // without wiping price/media (unlike the fully-unavailable path below).
          if (hasSizeOption && !variant) {
            this.productForm?.toggleSubmitButton(true, 'Select Size');
            return;
          }

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          if (hasSizeOption) {
            // Buy-buttons renders disabled for Size products, so derive ATC state
            // directly from the fully-resolved variant's availability instead.
            this.productForm?.toggleSubmitButton(!variant.available, window.variantStrings.soldOut);
          } else {
            this.productForm?.toggleSubmitButton(
              html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
              window.variantStrings.soldOut
            );
          }

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
